import type { ReactNode } from "react";

// Hooks & utilities
import { cn } from "@/lib/utils";

import { EmptyBoxIcon } from "../icons/EmptyBoxIcon";

interface EmptyStateProps {
  label: string;
  /**
   * Supporting copy under the label. Its presence promotes `label` from a 12px
   * grey line to an 18px heading — an empty state that explains itself reads as
   * a heading plus body, not as one long caption.
   */
  description?: string;
  /** Replaces the default {@link EmptyBoxIcon} — e.g. a key for API tokens. */
  icon?: ReactNode;
  children?: ReactNode;
  className?: string;
}

export function EmptyState({
  label,
  description,
  icon,
  children,
  className,
}: EmptyStateProps) {
  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center gap-4 rounded-input border border-dinnerplate-white bg-dinnerplate-white-lighter px-8 py-16",
        className,
      )}
    >
      {icon ?? <EmptyBoxIcon aria-hidden="true" />}
      {description ? (
        // A described empty state gets Figma's rhythm: 6px between the two
        // lines of copy, 24px before the action.
        <div className="flex flex-col items-center gap-8">
          <div className="flex flex-col items-center gap-2 text-center">
            <h3 className="font-inter text-lg font-semibold leading-normal text-off-black antialiased">
              {label}
            </h3>
            <p className="max-w-empty-state-copy font-inter text-sm font-normal leading-relaxed text-off-black antialiased">
              {description}
            </p>
          </div>
          {children}
        </div>
      ) : (
        <>
          <p className="font-inter text-xs font-normal leading-tight text-grey antialiased">
            {label}
          </p>
          {children}
        </>
      )}
    </div>
  );
}
